/**
 * You › Security › Lost a device (docs/e2e.md "Phone with one Face ID"): one click revokes the
 * lost device AND, by default, the synced passkey that trusted it. Revoking only the device leaves
 * that passkey usable from any phone it syncs to (one Face ID signs in and unlocks). Another
 * passkey signs the revocation; the account then rotates to a new encryption epoch.
 */
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@viko/transcript-ui'
import { ShieldOff } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { loadDevice } from '../lib/device-key.js'
import { planLostDevice, revokeLostDevice } from '../lib/lost-device.js'
import { passkeyErrorMessage, passkeys } from '../lib/passkeys.js'
import { qk, useCredentials, useDevices } from '../lib/queries.js'
import type { Credential, Device } from '../lib/types.js'
import { useToast } from './ui.js'

export function LostDeviceView({
  devices,
  credentials,
  mine,
  busy,
  onRevoke,
  onAddPasskey,
}: {
  devices: Device[]
  credentials: Credential[]
  mine: string | undefined
  busy: boolean
  onRevoke: (device: Device, revokePasskey: boolean) => void
  onAddPasskey: () => void
}) {
  const id = useId()
  const others = devices.filter((d) => !d.revoked_at && d.id !== mine)
  const [picked, setPicked] = useState<string>('')
  const [alsoPasskey, setAlsoPasskey] = useState(true)
  const device = others.find((d) => d.id === picked)
  const passkey = device && credentials.find((c) => c.id === device.credential_id)
  const synced = !!passkey?.backed_up
  const plan = device && planLostDevice(device, credentials, alsoPasskey && !passkey?.revoked)
  const blocked = !!plan?.credentialId && plan.signWith.length === 0
  if (!others.length)
    return (
      <p className="m-0 px-4 pb-3 text-[14px] text-text-3">
        No other trusted devices. Lost this one? Sign in elsewhere and revoke it there.
      </p>
    )
  return (
    <div className="grid gap-3 px-4 pb-3">
      <label className="field" htmlFor={`${id}-device`}>
        <span>Lost a device?</span>
      </label>
      <select
        id={`${id}-device`}
        className="input"
        value={picked}
        onChange={(e) => setPicked(e.target.value)}
      >
        <option value="">Choose the lost device…</option>
        {others.map((d) => (
          <option key={d.id} value={d.id}>
            {d.label}
          </option>
        ))}
      </select>
      {device ? (
        <>
          <p className="m-0 text-[15px] text-text-2">
            Revoking {device.label} signs it out, stops its commands and rotates your encryption
            keys so it can’t read anything new.
          </p>
          {passkey && !passkey.revoked ? (
            <label className="flex min-h-11 items-start gap-2.5 text-[15px]">
              <input
                type="checkbox"
                className="mt-0.5 h-5 w-5 accent-[var(--vk-accent)]"
                checked={alsoPasskey}
                onChange={(e) => setAlsoPasskey(e.target.checked)}
              />
              <span>
                Also revoke the passkey “{passkey.name ?? 'Passkey'}”.{' '}
                {synced
                  ? 'It is synced: anyone with that phone and its passcode could otherwise sign back in and unlock your work on another phone with one Face ID.'
                  : 'It trusted this device.'}{' '}
                Other browsers it trusted will ask for a passkey again.
              </span>
            </label>
          ) : null}
          {blocked ? (
            <div role="alert" className="grid gap-2 border-l-2 border-status-wait pl-3 text-[14px]">
              <span>
                That is your only passkey, and a passkey can’t revoke itself. Add a new passkey
                first; it signs the revocation.
              </span>
              <Button size="sm" onClick={onAddPasskey}>
                Add passkey
              </Button>
            </div>
          ) : null}
          <Button
            variant="danger"
            className="!min-h-11"
            icon={<ShieldOff size={15} />}
            disabled={busy || blocked}
            onClick={() => onRevoke(device, !!plan?.credentialId)}
          >
            {plan?.credentialId ? 'Revoke device and passkey' : 'Revoke device'}
          </Button>
        </>
      ) : null}
    </div>
  )
}

export function LostDevice({
  devices,
  credentials,
  mine,
  onAddPasskey,
}: {
  devices: Device[]
  credentials: Credential[]
  mine: string | undefined
  onAddPasskey: () => void
}) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  return (
    <LostDeviceView
      devices={devices}
      credentials={credentials}
      mine={mine}
      busy={busy}
      onAddPasskey={onAddPasskey}
      onRevoke={async (device, revokePasskey) => {
        if (!b.content) {
          toast('Unlock encrypted content on this browser first', 'error')
          return
        }
        setBusy(true)
        try {
          await revokeLostDevice(planLostDevice(device, credentials, revokePasskey), {
            session: b.content,
            api: b.api,
            passkeys,
          })
          await qc.invalidateQueries({ queryKey: qk.devices })
          await qc.invalidateQueries({ queryKey: qk.credentials })
          toast(
            revokePasskey ? `${device.label} and its passkey revoked` : `${device.label} revoked`,
          )
        } catch (e) {
          toast(passkeyErrorMessage(e), 'error')
        } finally {
          setBusy(false)
        }
      }}
    />
  )
}

/** Security-section entry: loads devices, passkeys and this browser's own device id. */
export function LostDeviceSection() {
  const devices = useDevices()
  const creds = useCredentials()
  const [mine, setMine] = useState<string>()
  useEffect(() => {
    void loadDevice().then((d) => setMine(d?.deviceKeyId))
  }, [])
  return (
    <LostDevice
      devices={devices.data ?? []}
      credentials={creds.data ?? []}
      mine={mine}
      onAddPasskey={() =>
        document.getElementById('passkeys')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    />
  )
}
